import { BzDateFns, bzExpect, isNullish } from '@breezy/shared'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSubscription } from 'urql'
import { trpc } from '../../hooks/trpc'
import { useStrictContext } from '../../utils/react-utils'
import { useExpectedPrincipal } from '../PrincipalUser'
import { TIME_CLOCK_PROVIDER_DATA_SUBSCRIPTION } from './TimeClockProvider.gql'

export type UserTimeClockCurrentAssignment = {
  activity: string
  jobGuid: string
  jobAppointmentGuid: string
  jobAppointmentAssignmentGuid: string
}

type TimeClockContextType = {
  currentAssignment?: UserTimeClockCurrentAssignment
  clockedInTime?: Date
  localElapsedDurationMin: number | undefined
  fetching: boolean
  clockIn: () => void
  clockOut: () => void
  refetch: () => void
}

const TimeClockContext = React.createContext<TimeClockContextType | undefined>(undefined)

export const useTimeClock = () => useStrictContext(TimeClockContext)

type TimeClockProviderProps = React.PropsWithChildren

export const TimeClockProvider = React.memo<TimeClockProviderProps>(({ children }) => {
  const principal = useExpectedPrincipal()
  const company = principal.company
  const companyGuid = company?.companyGuid ?? ''
  const userGuid = principal.userGuid ?? ''
  const tzId = company?.timezone ?? BzDateFns.UTC

  const [currentDay, setCurrentDay] = useState(() => BzDateFns.startOfDay(BzDateFns.now(tzId)))

  const startOfToday = useMemo(() => BzDateFns.formatISO(currentDay, tzId), [currentDay, tzId])

  const nextDayTransition = useRef<Date>(BzDateFns.addDays(BzDateFns.startOfDay(BzDateFns.now(tzId)), 1))

  const pause = !principal.company || !principal.userGuid

  // Track pending clock operations
  const [pendingOperation, setPendingOperation] = useState<'clockIn' | 'clockOut' | null>(null)

  const [res, refetch] = useSubscription({
    query: TIME_CLOCK_PROVIDER_DATA_SUBSCRIPTION,
    variables: {
      companyGuid,
      userGuid,
      startTime: startOfToday,
    },
    pause,
  })

  const clockInMutation = trpc.timesheets['timesheets:clock-in'].useMutation({
    onSuccess: () => {
      refetch()
      setPendingOperation(null)
    },
    onError: () => {
      setPendingOperation(null)
    },
  })

  const clockOutMutation = trpc.timesheets['timesheets:clock-out'].useMutation({
    onSuccess: () => {
      refetch()
      setPendingOperation(null)
      // Reset the timer immediately on successful clock out
      setLocalElapsedDurationMin(0)
    },
    onError: () => {
      setPendingOperation(null)
    },
  })

  // Check if the user is currently clocked in (has an entry with no finalEndTime)
  const hasOpenEntry = useMemo(() => {
    if (isNullish(res.data) || res.data.timesheetEntries.length === 0) {
      return false
    }

    // Look for any entry that doesn't have a finalEndTime
    return res.data.timesheetEntries.some(entry => isNullish(entry.finalEndTime))
  }, [res.data])

  const clockedInTime = useMemo<Date | undefined>(() => {
    if (!hasOpenEntry) {
      return undefined
    }

    // Find the open entry (the one without finalEndTime)
    const openEntry = res.data?.timesheetEntries.find(entry => isNullish(entry.finalEndTime))
    if (!openEntry) {
      return undefined
    }

    return BzDateFns.parseISO(bzExpect(openEntry.finalStartTime), tzId)
  }, [res.data, tzId, hasOpenEntry])

  const isClockedIn = useMemo(() => hasOpenEntry && !isNullish(clockedInTime), [hasOpenEntry, clockedInTime])

  const startingElapsedDuration: number = useMemo(() => {
    if (!isClockedIn || isNullish(res.data) || res.data.timesheetEntries.length === 0) {
      return 0
    }

    // Find the open entry
    const openEntry = res.data.timesheetEntries.find(entry => isNullish(entry.finalEndTime))
    if (!openEntry) {
      return 0
    }

    // Calculate elapsed time for the open entry
    const startTime = BzDateFns.parseISO(bzExpect(openEntry.finalStartTime), tzId)

    const now = BzDateFns.now(tzId)
    return Math.abs(BzDateFns.differenceInMinutes(startTime, now))
  }, [res.data, tzId, isClockedIn])

  const [localElapsedDurationMin, setLocalElapsedDurationMin] = useState<number>(startingElapsedDuration)

  const currentAssignment = useMemo<UserTimeClockCurrentAssignment | undefined>(() => {
    if (!isClockedIn || isNullish(res.data) || res.data.timesheetEntries.length === 0) {
      return undefined
    }

    // Find the open entry
    const openEntry = res.data.timesheetEntries.find(entry => isNullish(entry.finalEndTime))
    if (!openEntry) {
      return undefined
    }

    const { timesheetEntryLinkData, timesheetEntryActivity } = openEntry

    if (
      isNullish(timesheetEntryLinkData[0]?.jobGuid) ||
      isNullish(timesheetEntryLinkData[0]?.jobAppointmentGuid) ||
      isNullish(timesheetEntryLinkData[0]?.jobAppointmentAssignmentGuid)
    ) {
      return undefined
    }

    return {
      activity: bzExpect(timesheetEntryActivity?.activityName),
      jobGuid: bzExpect(timesheetEntryLinkData[0].jobGuid),
      jobAppointmentGuid: bzExpect(timesheetEntryLinkData[0].jobAppointmentGuid),
      jobAppointmentAssignmentGuid: bzExpect(timesheetEntryLinkData[0].jobAppointmentAssignmentGuid),
    }
  }, [res.data, isClockedIn])

  // Update local timer when server data changes
  useEffect(() => {
    setLocalElapsedDurationMin(startingElapsedDuration)
  }, [startingElapsedDuration])

  // Effect to check for day transition
  useEffect(() => {
    const checkDayTransition = () => {
      const now = BzDateFns.now(tzId)

      // If we've passed the next day transition point
      if (BzDateFns.isAfter(now, nextDayTransition.current)) {
        // Update the current day
        const newCurrentDay = BzDateFns.startOfDay(now)
        setCurrentDay(newCurrentDay)

        // Set the next transition point
        nextDayTransition.current = BzDateFns.addDays(newCurrentDay, 1)

        // Refetch data with the new day
        refetch()
      }
    }

    // Check for day transition every minute
    const dayTransitionInterval = setInterval(checkDayTransition, 1000 * 60)

    return () => clearInterval(dayTransitionInterval)
  }, [tzId, refetch])

  // Effect to update timer every minute when clocked in
  useEffect(() => {
    let intervalId: NodeJS.Timeout

    if (isClockedIn) {
      intervalId = setInterval(() => {
        setLocalElapsedDurationMin(prev => prev + 1)
      }, 1000 * 60)
    } else {
      // Reset elapsed time when not clocked in
      setLocalElapsedDurationMin(0)
    }

    return () => {
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [isClockedIn])

  // Apply data updates when subscription data changes
  useEffect(() => {
    if (res.data) {
      // Check if we need to update any other state based on subscription updates
      if (pendingOperation === 'clockOut' && !isClockedIn) {
        // Successfully clocked out
        setPendingOperation(null)
      } else if (pendingOperation === 'clockIn' && isClockedIn) {
        // Successfully clocked in
        setPendingOperation(null)
      }
    }
  }, [res.data, isClockedIn, pendingOperation])

  const clockIn = useCallback(() => {
    setPendingOperation('clockIn')
    clockInMutation.mutate({})
  }, [clockInMutation])

  const clockOut = useCallback(() => {
    setPendingOperation('clockOut')
    clockOutMutation.mutate({})
  }, [clockOutMutation])

  const fetching =
    res.fetching || clockInMutation.isLoading || clockOutMutation.isLoading || !isNullish(pendingOperation)

  return (
    <TimeClockContext.Provider
      value={{
        currentAssignment,
        clockedInTime,
        localElapsedDurationMin: isClockedIn ? localElapsedDurationMin : undefined,
        refetch,
        fetching,
        clockIn,
        clockOut,
      }}
    >
      {children}
    </TimeClockContext.Provider>
  )
})

TimeClockProvider.displayName = 'TimeClockProvider'
